/**
 * DemoMap - Clear navigation map with route visualization
 */

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigationStore } from '@/stores/useNavigationStore';
import { useAirQualityStore, useLocationStore } from '@/stores';
import { cn } from '@/utils/cn';

interface DemoMapProps {
  className?: string;
}

// Street data for the demo map
const STREETS = {
  horizontal: [
    { y: 15, name: 'HARBOR BLVD', ni: 92 },
    { y: 35, name: 'MARKET ST', ni: 78 },
    { y: 55, name: 'MISSION ST', ni: 65 },
    { y: 75, name: 'HOWARD ST', ni: 85 },
  ],
  vertical: [
    { x: 15, name: '1ST ST', ni: 88 },
    { x: 35, name: '2ND ST', ni: 72 },
    { x: 55, name: '3RD ST', ni: 58 },
    { x: 75, name: '4TH ST', ni: 82 },
  ],
};

// Points of interest
const POIS = [
  { x: 10, y: 10, name: 'Waterfront Park', type: 'park', ni: 95 },
  { x: 85, y: 20, name: 'City Hall', type: 'landmark', ni: 75 },
  { x: 25, y: 65, name: 'Industrial Zone', type: 'danger', ni: 45 },
  { x: 70, y: 70, name: 'Green Square', type: 'park', ni: 90 },
];

export function DemoMap({ className }: DemoMapProps) {
  const [userPos, setUserPos] = useState({ x: 50, y: 75 }); // Start position

  const isNavigating = useNavigationStore((state) => state.isNavigating);
  const activeRoute = useNavigationStore((state) => state.activeRoute);
  const selectedDestination = useNavigationStore((state) => state.selectedDestination);
  const aqi = useAirQualityStore((state) => state.currentReading?.aqi) || 42;
  const currentPosition = useLocationStore((state) => state.currentPosition);

  // Destination position (demo)
  const destPos = { x: 15, y: 20 }; // Near Waterfront Park

  // Simulate movement during navigation
  useEffect(() => {
    if (!isNavigating) return;

    const interval = setInterval(() => {
      setUserPos((prev) => {
        // Move toward destination
        const dx = destPos.x - prev.x;
        const dy = destPos.y - prev.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < 2) return prev; // Arrived

        const speed = 0.5;
        return {
          x: prev.x + (dx / dist) * speed,
          y: prev.y + (dy / dist) * speed,
        };
      });
    }, 100);

    return () => clearInterval(interval);
  }, [isNavigating]);


  return (
    <div className={cn('relative w-full h-full bg-[#0a1628] overflow-hidden', className)}>
      {/* Map Container */}
      <div
        className="absolute inset-0 transition-transform duration-300"
        style={{
          transform: 'scale(1)',
          transformOrigin: `${userPos.x}% ${userPos.y}%`,
        }}
      >
        {/* Map Background */}
        <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice">
          {/* Water area (top) */}
          <rect x="0" y="0" width="100" height="12" fill="#0d2137" />
          <text x="50" y="6" fill="#1a4a7a" fontSize="2" textAnchor="middle" fontFamily="monospace">
            ~ ~ ~ BAY ~ ~ ~
          </text>

          {/* City blocks background */}
          <rect x="0" y="12" width="100" height="88" fill="#0f1a2a" />

          {/* Horizontal streets */}
          {STREETS.horizontal.map((street) => (
            <g key={street.name}>
              <rect
                x="0"
                y={street.y - 2}
                width="100"
                height="4"
                fill="#1a2540"
              />
              <line
                x1="0"
                y1={street.y}
                x2="100"
                y2={street.y}
                stroke="#2a3a5a"
                strokeWidth="0.3"
                strokeDasharray="2 1"
              />
              <text
                x="2"
                y={street.y + 0.5}
                fill="#4a6a8a"
                fontSize="1.5"
                fontFamily="monospace"
              >
                {street.name}
              </text>
              {/* NI indicator on street */}
              <circle
                cx="95"
                cy={street.y}
                r="1.5"
                fill={street.ni >= 80 ? '#00ff88' : street.ni >= 60 ? '#ffaa00' : '#ff3366'}
              />
              <text
                x="95"
                y={street.y + 0.4}
                fill="#0a1628"
                fontSize="1"
                textAnchor="middle"
                fontWeight="bold"
              >
                {street.ni}
              </text>
            </g>
          ))}

          {/* Vertical streets */}
          {STREETS.vertical.map((street) => (
            <g key={street.name}>
              <rect
                x={street.x - 2}
                y="12"
                width="4"
                height="88"
                fill="#1a2540"
              />
              <line
                x1={street.x}
                y1="12"
                x2={street.x}
                y2="100"
                stroke="#2a3a5a"
                strokeWidth="0.3"
                strokeDasharray="2 1"
              />
              <text
                x={street.x}
                y="98"
                fill="#4a6a8a"
                fontSize="1.5"
                textAnchor="middle"
                fontFamily="monospace"
                transform={`rotate(-90, ${street.x}, 98)`}
              >
                {street.name}
              </text>
            </g>
          ))}

          {/* Points of Interest */}
          {POIS.map((poi) => (
            <g key={poi.name}>
              {poi.type === 'park' && (
                <>
                  <rect
                    x={poi.x - 5}
                    y={poi.y - 5}
                    width="10"
                    height="10"
                    rx="1"
                    fill="#0a3020"
                    stroke="#00ff88"
                    strokeWidth="0.3"
                  />
                  <text
                    x={poi.x}
                    y={poi.y}
                    fill="#00ff88"
                    fontSize="1.2"
                    textAnchor="middle"
                    fontFamily="monospace"
                  >
                    🌳
                  </text>
                  <text
                    x={poi.x}
                    y={poi.y + 3}
                    fill="#00ff88"
                    fontSize="1"
                    textAnchor="middle"
                    fontFamily="monospace"
                  >
                    {poi.name}
                  </text>
                  <text
                    x={poi.x}
                    y={poi.y + 4.5}
                    fill="#00ff88"
                    fontSize="0.8"
                    textAnchor="middle"
                  >
                    AQI: {100 - poi.ni}
                  </text>
                </>
              )}
              {poi.type === 'danger' && (
                <>
                  <rect
                    x={poi.x - 6}
                    y={poi.y - 5}
                    width="12"
                    height="10"
                    rx="1"
                    fill="#3a1520"
                    stroke="#ff3366"
                    strokeWidth="0.3"
                  />
                  <text
                    x={poi.x}
                    y={poi.y}
                    fill="#ff3366"
                    fontSize="1.2"
                    textAnchor="middle"
                  >
                    ⚠️
                  </text>
                  <text
                    x={poi.x}
                    y={poi.y + 3}
                    fill="#ff3366"
                    fontSize="1"
                    textAnchor="middle"
                    fontFamily="monospace"
                  >
                    {poi.name}
                  </text>
                  <text
                    x={poi.x}
                    y={poi.y + 4.5}
                    fill="#ff3366"
                    fontSize="0.8"
                    textAnchor="middle"
                  >
                    AQI: {100 - poi.ni} ⚠️
                  </text>
                </>
              )}
              {poi.type === 'landmark' && (
                <>
                  <rect
                    x={poi.x - 5}
                    y={poi.y - 4}
                    width="10"
                    height="8"
                    rx="1"
                    fill="#2a2a40"
                    stroke="#00ffd5"
                    strokeWidth="0.3"
                  />
                  <text
                    x={poi.x}
                    y={poi.y + 2}
                    fill="#00ffd5"
                    fontSize="1"
                    textAnchor="middle"
                    fontFamily="monospace"
                  >
                    {poi.name}
                  </text>
                </>
              )}
            </g>
          ))}

          {/* Route path (when navigating or destination selected) */}
          {(isNavigating || selectedDestination) && (
            <g>
              {/* Route background */}
              <motion.path
                d={`M ${userPos.x} ${userPos.y}
                   L ${userPos.x} ${STREETS.horizontal[0].y}
                   L ${destPos.x} ${STREETS.horizontal[0].y}
                   L ${destPos.x} ${destPos.y}`}
                fill="none"
                stroke="#00ff88"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.3"
              />
              {/* Animated route line */}
              <motion.path
                d={`M ${userPos.x} ${userPos.y}
                   L ${userPos.x} ${STREETS.horizontal[0].y}
                   L ${destPos.x} ${STREETS.horizontal[0].y}
                   L ${destPos.x} ${destPos.y}`}
                fill="none"
                stroke="#00ff88"
                strokeWidth="0.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="3 2"
                animate={{ strokeDashoffset: [0, -10] }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              />
              {/* Route label */}
              <rect x="30" y="12" width="16" height="4" rx="0.5" fill="#00ff88" />
              <text x="38" y="14.5" fill="#0a1628" fontSize="1.2" textAnchor="middle" fontWeight="bold">
                SAFE ROUTE
              </text>
            </g>
          )}

          {/* User location */}
          <g>
            {/* Accuracy circle */}
            <motion.circle
              cx={userPos.x}
              cy={userPos.y}
              r="3"
              fill={aqi <= 50 ? '#00ff88' : aqi <= 100 ? '#ffaa00' : '#ff3366'}
              opacity="0.2"
              animate={{ r: [3, 5, 3] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            {/* User dot */}
            <circle
              cx={userPos.x}
              cy={userPos.y}
              r="1.5"
              fill={aqi <= 50 ? '#00ff88' : aqi <= 100 ? '#ffaa00' : '#ff3366'}
              stroke="#ffffff"
              strokeWidth="0.4"
            />
            {/* Direction indicator */}
            {isNavigating && (
              <polygon
                points={`${userPos.x},${userPos.y - 3} ${userPos.x - 1},${userPos.y - 1} ${userPos.x + 1},${userPos.y - 1}`}
                fill="#00ff88"
              />
            )}
            {/* Label */}
            <text
              x={userPos.x}
              y={userPos.y + 4}
              fill="#ffffff"
              fontSize="1.2"
              textAnchor="middle"
              fontWeight="bold"
            >
              YOU
            </text>
          </g>

          {/* Destination marker */}
          {(selectedDestination || isNavigating) && (
            <g>
              <motion.circle
                cx={destPos.x}
                cy={destPos.y}
                r="2"
                fill="none"
                stroke="#00ffd5"
                strokeWidth="0.5"
                animate={{ r: [2, 4, 2], opacity: [1, 0.3, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              />
              <circle
                cx={destPos.x}
                cy={destPos.y}
                r="1.2"
                fill="#00ffd5"
              />
              <text
                x={destPos.x}
                y={destPos.y + 4}
                fill="#00ffd5"
                fontSize="1.2"
                textAnchor="middle"
                fontWeight="bold"
              >
                {selectedDestination?.name || 'DESTINATION'}
              </text>
            </g>
          )}
        </svg>
      </div>

      {/* Legend - positioned at bottom-right above map controls */}
      <div className="absolute bottom-28 right-4 z-10 bg-glass-bg border border-glass-border rounded-lg p-3 backdrop-blur-md">
        <div className="text-xs font-hud text-text-muted uppercase mb-2">Air Quality Legend</div>
        <div className="flex flex-col gap-1.5 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-hud-success" />
            <span className="text-text-secondary">0-50: Good (Safe)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-hud-warning" />
            <span className="text-text-secondary">51-100: Moderate</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-hud-danger" />
            <span className="text-text-secondary">&gt;100: Unhealthy</span>
          </div>
        </div>
      </div>

      {/* Current Street Info */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20">
        <div className="bg-glass-bg border border-glass-border rounded-lg px-4 py-2 backdrop-blur-md text-center">
          <div className="text-xs text-text-muted uppercase">Current Location</div>
          <div className="text-sm font-bold text-text-primary">
            {currentPosition
              ? `${currentPosition.lat.toFixed(4)}°, ${currentPosition.lng.toFixed(4)}°`
              : 'Getting location...'}
          </div>
          <div className={cn(
            'text-xs font-bold',
            aqi <= 50 ? 'text-hud-success' : aqi <= 100 ? 'text-hud-warning' : 'text-hud-danger'
          )}>
            Air Quality: {aqi} AQI
          </div>
        </div>
      </div>

      {/* Demo Mode Badge - positioned below HUD */}
      <div className="absolute top-52 left-4 z-20">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-hud-secondary/20 border border-hud-secondary/30 rounded-full">
          <div className="w-2 h-2 rounded-full bg-hud-secondary animate-pulse" />
          <span className="text-xs font-hud text-hud-secondary uppercase tracking-wider">
            Demo Mode
          </span>
        </div>
      </div>

      {/* Navigation Info (when navigating) */}
      {isNavigating && activeRoute && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-20">
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="bg-hud-success text-bg-deep px-4 py-2 rounded-lg font-bold text-center"
          >
            <div className="text-lg">↑ Head North on HOWARD ST</div>
            <div className="text-sm opacity-80">Then turn left onto 1ST ST</div>
          </motion.div>
        </div>
      )}
    </div>
  );
}


export default DemoMap;
